import { ChevronUp, ChevronDown } from "lucide-react";

/**
 * Floating ↑/↓ nudge pills for a scrollable source panel. Especially useful
 * when auto-scroll is off.
 *
 *   variant="sticky"   → renders inside a scrollable panel; nudges its own
 *                        scrollHeight. Falls back to window scroll if the
 *                        target isn't overflowed.
 *   variant="floating" → fixed to the viewport's right edge; always nudges
 *                        the window. Perfect for full-page paper views like
 *                        the Legal source that don't have their own scroll.
 *
 * `step` is the pixel amount per nudge (default 200 — one comfortable line group).
 */
export default function SourceScrollNudge({
  targetRef,
  step = 200,
  variant = "sticky",
  testId = "source-scroll-nudge",
}) {
  const nudge = (dir) => {
    const el = targetRef?.current;
    if (el && el.scrollHeight > el.clientHeight + 1) {
      el.scrollBy({ top: dir * step, behavior: "smooth" });
    } else {
      window.scrollBy({ top: dir * step, behavior: "smooth" });
    }
  };

  const wrapperClass =
    variant === "floating"
      ? "fixed right-4 top-1/2 -translate-y-1/2 flex flex-col gap-2 z-30 pointer-events-auto"
      : "sticky float-right bottom-2 mr-1 mt-1 flex flex-col gap-1 z-10 pointer-events-auto";

  return (
    <div
      className={wrapperClass}
      style={variant === "sticky" ? { top: "calc(100% - 88px)" } : undefined}
      data-testid={testId}
      aria-label="Scroll source manually"
    >
      <button
        type="button"
        onClick={() => nudge(-1)}
        className="w-9 h-9 flex items-center justify-center rounded-full border-2 border-brand-gold bg-background/95 text-brand-gold shadow-md hover:bg-brand-gold hover:text-background transition-colors"
        data-testid={`${testId}-up`}
        aria-label="Scroll source up"
        title="Scroll source up"
      >
        <ChevronUp className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={() => nudge(1)}
        className="w-9 h-9 flex items-center justify-center rounded-full border-2 border-brand-gold bg-background/95 text-brand-gold shadow-md hover:bg-brand-gold hover:text-background transition-colors"
        data-testid={`${testId}-down`}
        aria-label="Scroll source down"
        title="Scroll source down"
      >
        <ChevronDown className="w-4 h-4" />
      </button>
    </div>
  );
}
